import type { AssessmentType, AttachmentCategory, AttachmentSelectDto, SurveyStatus } from '@@types/api.types';
import type { CategoriesQueryParams } from './admin-categories.query';
import type { DomainsQueryParams } from './admin-domains.query';

type AttachmentTargetIdKey = Extract<keyof AttachmentSelectDto, 'answerId' | 'productId' | 'technologyId'>;

export type AttachmentTargetType = AttachmentTargetIdKey extends `${infer TargetType}Id` ? TargetType : never;

export const queryKeys = {
  adminCompanies: {
    root: ['adminCompanies'] as const,
    getCompanies: (params: Record<string, unknown>) => ['adminCompanies', 'getCompanies', params],
    getCompanyById: (companyId: string) => ['adminCompanies', 'getCompanyById', companyId],
    getHierarchyOptions: (companyId: string) => ['adminCompanies', 'getHierarchyOptions', companyId],
    getMemberCandidates: (companyId: string, search: string) => [
      'adminCompanies',
      'memberCandidates',
      companyId,
      search,
    ],
    getParentOrganizations: (params?: { includeDeleted?: boolean; tagNames?: string[] }) => [
      'adminCompanies',
      'getParentOrganizations',
      params?.includeDeleted ?? false,
      [...(params?.tagNames ?? [])]
        .map((tagName) => tagName.trim().toLowerCase())
        .sort((a, b) => a.localeCompare(b))
        .join('|'),
    ],
  },
  intelOrganization: {
    root: ['intelOrganization'] as const,
    members: (companyId?: string | null) => ['intelOrganization', 'members', companyId ?? 'no-company'] as const,
  },
  adminUsers: {
    root: ['adminUsers'] as const,
    getUsers: (params: Record<string, unknown>) => ['adminUsers', 'getUsers', params],
    getUserById: (userId: string) => ['adminUsers', 'getUserById', userId],
  },
  adminCategories: {
    root: ['adminCategories'] as const,
    getCategories: (params?: CategoriesQueryParams) => ['adminCategories', 'getCategories', params].filter(Boolean),
    getCategoryById: (categoryId: string) => ['adminCategories', 'getCategoryById', categoryId],
    getAvailableParents: (categoryId?: string) =>
      ['adminCategories', 'getAvailableParents', categoryId].filter(Boolean),
    getAvailableSubcategories: () => ['adminCategories', 'getAvailableSubcategories'],
  },
  adminDomains: {
    root: ['adminDomains'] as const,
    getDomains: (params?: DomainsQueryParams) => ['adminDomains', 'getDomains', params].filter(Boolean),
    getDomainById: (domainId: string) => ['adminDomains', 'getDomainById', domainId],
    getAvailableParents: (domainId?: string) => ['adminDomains', 'getAvailableParents', domainId].filter(Boolean),
    getAvailableSubdomains: () => ['adminDomains', 'getAvailableSubdomains'],
  },
  adminUses: {
    root: ['adminUses'] as const,
    getUseById: (useId: string) => ['adminUses', 'getUseById', useId],
    getUses: (params?: Record<string, unknown>) => ['adminUses', 'getUses', params].filter(Boolean),
  },
  llm: {
    adminRoot: ['llm', 'admin'] as const,
    getModels: ['llm', 'getModels'],
    getAdminLogs: (params: { page?: number | null; limit?: number | null }) =>
      ['llm', 'admin', 'getLogs', params.page, params.limit].filter(Boolean),
    getAdminLogById: (logId: string) => ['llm', 'admin', 'getLogById', logId],
    getAdminPromptByKey: (params: { key: string; version?: number }) => [
      'llm',
      'admin',
      'getPromptByKey',
      params.key,
      params.version ?? 'latest',
    ],
    getAdminPromptByKeyPrefix: (key: string) => ['llm', 'admin', 'getPromptByKey', key] as const,
    getAdminPrompts: ['llm', 'admin', 'getPrompts'],
  },
  assessments: {
    getAssessments: (type?: AssessmentType[], technologyId?: string) =>
      ['assessments', 'getAssessments', type, technologyId].filter(Boolean),
  },
  answers: {
    getAnswer: (sectionId: string, surveyId: string, questionId: string) => [
      'answers',
      'getAnswer',
      sectionId,
      surveyId,
      questionId,
    ],
    getSurveyAnswers: (surveyId: string) => ['answers', 'getSurveyAnswers', surveyId],
  },
  attachments: {
    getAnswerAttachments: (params: { answerId: string; category?: AttachmentCategory }) =>
      ['attachments', 'getAnswerAttachments', params.answerId, params.category].filter(Boolean),
    getProductAttachments: (params: { category?: AttachmentCategory; productId: string }) =>
      ['attachments', 'getProductAttachments', params.productId, params.category].filter(Boolean),
    getTechnologyAttachments: (params: { category?: AttachmentCategory; technologyId: string }) =>
      ['attachments', 'getTechnologyAttachments', params.technologyId, params.category].filter(Boolean),
  },
  category: {
    getCategories: ['category', 'getCategories'],
  },
  trlFilterQuestions: {
    getTrlFilterQuestions: ['trlFilterQuestions', 'getTrlFilterQuestions'],
  },
  capability: {
    getCapabilities: ['capability', 'getCapabilities'],
  },
  campaigns: {
    getCampaign: (campaignId: string) => ['campaigns', 'getCampaign', campaignId],
    getCampaigns: ['campaigns', 'getCampaigns'],
  },
  challenges: {
    getChallenges: ['challenges', 'getChallenges'],
    getChallenge: (challengeId: string) => ['challenges', 'getChallenge', challengeId],
  },
  digitalIntegration: {
    getDigitalIntegrations: ['digitalIntegration', 'getDigitalIntegrations'],
  },
  operationalIntegration: {
    getOperationalIntegrations: ['operationalIntegration', 'getOperationalIntegrations'],
  },
  physicalIntegration: {
    getPhysicalIntegrations: ['physicalIntegration', 'getPhysicalIntegrations'],
  },
  community: {
    getCommunities: ['community', 'getCommunities'],
  },

  companyProfile: {
    getCompanyProfile: (companyId: string) => ['companyProfile', 'getCompanyProfile', companyId],
    getCompanyProfiles: ['companyProfile', 'getCompanyProfiles'],
  },
  domain: {
    getDomains: ['domain', 'getDomains'],
  },
  effect: {
    getEffects: ['effect', 'getEffects'],
  },
  expertProfile: {
    getExpertProfile: ['expertProfile', 'getExpertProfile'],
    getExpertProfileForUser: (userId: string) => ['expertProfile', 'getExpertProfileForUser', userId],
  },
  experts: {
    getExpert: (expertId: string) => ['experts', 'getExpert', expertId],
    getExperts: ['experts', 'getExperts'],
  },
  jobRoles: {
    getJobRoles: ['jobRoles', 'getJobRoles'],
  },
  killChain: {
    getKillChains: ['killChain', 'getKillChains'],
  },
  meia: {
    getArchitectureAlternatives: (studyId: string) => ['meia', 'getArchitectureAlternatives', studyId],
    getInvestigativeQuestionTemplates: ['meia', 'getInvestigativeQuestionTemplates'],
    getKopTemplates: ['meia', 'getKopTemplates'],
    getMeiaSearch: (q: string) => ['meia', 'getMeiaSearch', q],
    getMissionPurposeTemplates: ['meia', 'getMissionPurposeTemplates'],
    getMissionRedActivityBindings: (missionId: string) => ['meia', 'getMissionRedActivityBindings', missionId],
    getMissionThreads: (missionId: string) => ['meia', 'getMissionThreads', missionId],
    getMoeEntries: (mosId: string) => ['meia', 'getMoeEntries', mosId],
    getMoeTemplates: ['meia', 'getMoeTemplates'],
    getMopEntries: (missionId: string) => ['meia', 'getMopEntries', missionId],
    getMopTemplates: ['meia', 'getMopTemplates'],
    getMosEntries: (missionId: string) => ['meia', 'getMosEntries', missionId],
    getMosTemplates: ['meia', 'getMosTemplates'],
    getRedForceActivities: ['meia', 'getRedForceActivities'],
    getRunCaseDashboard: (params: { runCaseId: string; studyId: string }) => [
      'meia',
      'getRunCaseDashboard',
      params.studyId,
      params.runCaseId,
    ],
    getRunCaseBriefAnalysis: (params: {
      missionId?: string;
      runCaseId: string;
      studyId: string;
      threadId?: string;
    }) => [
      'meia',
      'getRunCaseBriefAnalysis',
      params.studyId,
      params.runCaseId,
      params.missionId ?? 'study',
      params.threadId ?? 'aggregate',
    ],
    getRunCaseCostEffectivenessResults: (params: { runCaseId: string; studyId: string }) => [
      'meia',
      'getRunCaseCostEffectivenessResults',
      params.studyId,
      params.runCaseId,
    ],
    getRunCaseSensitivityResults: (params: { runCaseId: string; studyId: string }) => [
      'meia',
      'getRunCaseSensitivityResults',
      params.studyId,
      params.runCaseId,
    ],
    getRunCases: (params: { missionId?: string; studyId: string }) => [
      'meia',
      'getRunCases',
      params.studyId,
      params.missionId ?? 'study',
    ],
    getScoringOutputs: (params: { runCaseId?: string; studyId: string }) => [
      'meia',
      'getScoringOutputs',
      params.studyId,
      params.runCaseId ?? 'all',
    ],
    getStakeholderTemplates: ['meia', 'getStakeholderTemplates'],
    getStudyInvestigativeQuestions: (studyId: string) => ['meia', 'getStudyInvestigativeQuestions', studyId],
    getAcrRegister: (studyId: string) => ['meia', 'getAcrRegister', studyId],
    getStudy: (studyId: string) => ['meia', 'getStudy', studyId],
    getStudyP4E2eRows: (studyId: string) => ['meia', 'getStudyP4E2eRows', studyId],
    getStudyThreatTargets: (studyId: string) => ['meia', 'getStudyThreatTargets', studyId],
    getStudyGateReadiness: (params: { gate: string; missionId?: string; studyId: string }) => [
      'meia',
      'getStudyGateReadiness',
      params.studyId,
      params.gate,
      params.missionId ?? 'study',
    ],
    getStudyGateAudit: (params: { gate: string; missionId?: string; studyId: string }) => [
      'meia',
      'getStudyGateAudit',
      params.studyId,
      params.gate,
      params.missionId ?? 'study',
    ],
    getStudyGateSignoffReadiness: (params: { gate: string; studyId: string }) => [
      'meia',
      'getStudyGateSignoffReadiness',
      params.studyId,
      params.gate,
    ],
    getStudyMissions: (studyId: string) => ['meia', 'getStudyMissions', studyId],
    getStudySignoffs: (studyId: string) => ['meia', 'getStudySignoffs', studyId],
    getStudyStakeholderCandidates: (studyId: string) => ['meia', 'getStudyStakeholderCandidates', studyId],
    getStudyStakeholders: (studyId: string) => ['meia', 'getStudyStakeholders', studyId],
    getStudyValidationResults: (studyId: string) => ['meia', 'getStudyValidationResults', studyId],
    getMissions: ['meia', 'getMissions'] as const,
    getStudies: ['meia', 'getStudies'],
    getSwimlaneMosEntries: (swimlaneId: string) => ['meia', 'getSwimlaneMosEntries', swimlaneId],
    getSwimlaneTasks: (swimlaneId: string) => ['meia', 'getSwimlaneTasks', swimlaneId],
    getTaskAttachableTechnologies: (params: object) => ['meia', 'getTaskAttachableTechnologies', params],
    getTaskMoes: (taskId: string) => ['meia', 'getTaskMoes', taskId],
    getTaskMops: (taskId: string) => ['meia', 'getTaskMops', taskId],
    getTaskMoses: (taskId: string) => ['meia', 'getTaskMoses', taskId],
    getTaskTechnologyEngagements: (taskTechnologyId: string) => [
      'meia',
      'getTaskTechnologyEngagements',
      taskTechnologyId,
    ],
    getTaskTechnologies: (params: { studyId: string; taskId: string }) => [
      'meia',
      'getTaskTechnologies',
      params.studyId,
      params.taskId,
    ],
    getTargetEffectOverrides: (studyId: string) => ['meia', 'getTargetEffectOverrides', studyId],
    getStudyTargetEngagements: (studyId: string) => ['meia', 'getStudyTargetEngagements', studyId],
    getThreatTargetSystems: ['meia', 'getThreatTargetSystems'],
    getThreadTaskReadiness: (threadId: string) => ['meia', 'getThreadTaskReadiness', threadId],
    getThreadSwimlanes: (threadId: string) => ['meia', 'getThreadSwimlanes', threadId],
  },
  missionAssessments: {
    getDefinitions: ['missionAssessments', 'getDefinitions'],
    getLatestMissionAssessmentForTechnology: (technologyId: string) => [
      'missionAssessments',
      'getLatestMissionAssessmentForTechnology',
      technologyId,
    ],
    getLatestMissionAssessmentForTechnologyForExpert: (technologyId: string) => [
      'missionAssessments',
      'getLatestMissionAssessmentForTechnologyForExpert',
      technologyId,
    ],
    getLatestMissionAssessmentForTechnologyForIntel: (technologyId: string) => [
      'missionAssessments',
      'getLatestMissionAssessmentForTechnologyForIntel',
      technologyId,
    ],
    getLatestMissionAssessmentsByCompany: ['missionAssessments', 'getLatestMissionAssessmentsByCompany'],
    getMissionAssessment: (missionAssessmentId: string) => [
      'missionAssessments',
      'getMissionAssessment',
      missionAssessmentId,
    ],
    getMissionAssessmentForCompany: (missionAssessmentId: string) => [
      'missionAssessments',
      'getMissionAssessmentForCompany',
      missionAssessmentId,
    ],
    getMissionAssessmentsForExpert: ['missionAssessments', 'getMissionAssessmentsForExpert'],
    getVersionsByTechnology: (technologyId: string) => ['missionAssessments', 'getVersionsByTechnology', technologyId],
    getVersionsByTechnologyForExpert: (technologyId: string) => [
      'missionAssessments',
      'getVersionsByTechnologyForExpert',
      technologyId,
    ],
    getVersionsByTechnologyForIntel: (technologyId: string) => [
      'missionAssessments',
      'getVersionsByTechnologyForIntel',
      technologyId,
    ],
    // New per-section query keys
    getAllForExpert: ['missionAssessments', 'getAllForExpert'],
    getCore: (missionAssessmentId: string) => ['missionAssessments', 'getCore', missionAssessmentId],
    getEffects: (missionAssessmentId: string) => ['missionAssessments', 'getEffects', missionAssessmentId],
    getInters: (missionAssessmentId: string) => ['missionAssessments', 'getInters', missionAssessmentId],
    getLatestByTechnology: (technologyId: string) => ['missionAssessments', 'getLatestByTechnology', technologyId],
    getMetadata: (missionAssessmentId: string) => ['missionAssessments', 'getMetadata', missionAssessmentId],
    getServices: (missionAssessmentId: string) => ['missionAssessments', 'getServices', missionAssessmentId],
  },
  missionAssessmentDrafts: {
    getDraft: (draftId: string) => ['missionAssessmentDrafts', 'getDraft', draftId],
    getDraftsForExpert: ['missionAssessmentDrafts', 'getDraftsForExpert'],
    getDraftForTechnology: (expertId: string, technologyId: string, requestId: string) => [
      'missionAssessmentDrafts',
      'getDraftForTechnology',
      expertId,
      technologyId,
      requestId,
    ],
    getDraftVersionsByTechnology: (technologyId: string) => [
      'missionAssessmentDrafts',
      'getDraftVersionsByTechnology',
      technologyId,
    ],
    getDraftVersionsByRequest: (requestId: string) => [
      'missionAssessmentDrafts',
      'getDraftVersionsByRequest',
      requestId,
    ],
  },
  missionAssessmentRequests: {
    getMissionAssessmentRequest: (id: string) => ['missionAssessmentRequests', 'getMissionAssessmentRequest', id],
    getMissionAssessmentRequests: ['missionAssessmentRequests', 'getMissionAssessmentRequests'],
    getLatestMissionAssessmentRequestForTechnology: (technologyId: string) => [
      'missionAssessmentRequests',
      'getLatestMissionAssessmentRequestForTechnology',
      technologyId,
    ],
  },
  placeholders: {
    default: ['placeholders', 'default'],
  },
  parentOrganizations: {
    getPortfolio: (params: Record<string, unknown>) => ['parentOrganizations', 'getPortfolio', params],
    getProjects: ['parentOrganizations', 'getProjects'],
  },
  product: {
    getProduct: (productId: string) => ['product', 'getProduct', productId],
    getProducts: (technologyId?: string) => ['product', 'getProducts', technologyId].filter(Boolean),
  },
  service: {
    getServices: ['service', 'getServices'],
  },
  survey: {
    getSurvey: (surveyId: string) => ['survey', 'getSurvey', surveyId],
    getSurveys: (technologyId?: string, status?: SurveyStatus) =>
      ['survey', 'getSurveys', technologyId, status].filter(Boolean),
  },
  targetSystem: {
    getTargetSystems: ['targetSystem', 'getTargetSystems'],
  },
  technology: {
    getAssessmentHistory: (technologyId: string) => ['technology', 'getAssessmentHistory', technologyId],
    getContributors: (technologyId: string) => ['technology', 'getContributors', technologyId],
    getTechnologies: ['technology', 'getTechnologies'],
    getTechnologiesList: ['technology', 'getTechnologiesList'],
    getTechnology: (technologyId: string) => ['technology', 'getTechnology', technologyId],

    getTechnologiesAssessments: (type?: AssessmentType) =>
      ['technology', 'getTechnologiesAssessments', type].filter(Boolean),

    getTechnologyTrlAssessment: (technologyId: string) => ['technology', 'getTechnologyTrlAssessment', technologyId],
    getTechnologyMrlAssessment: (technologyId: string) => ['technology', 'getTechnologyMrlAssessment', technologyId],
    getTechnologyCyberAssessments: (technologyId: string) => [
      'technology',
      'getTechnologyCyberAssessments',
      technologyId,
    ],
    getCyberAssessment: (cyberAssessmentId: string) => ['technology', 'getCyberAssessment', cyberAssessmentId],
    getTechnologyTrlAnalysis: (technologyId: string) => ['technology', 'getTechnologyTrlAnalysis', technologyId],
    getTechnologyTrlProjection: (technologyId: string) => ['technology', 'getTechnologyTrlProjection', technologyId],
    getTechnologyCategories: ['technology', 'getTechnologyCategories'],
    getTechnologyDomains: ['technology', 'getTechnologyDomains'],
    getTechnologyUses: ['technology', 'getTechnologyUses'],
    getTechnologyUsesWithAssessments: (technologyId: string) => [
      'technology',
      'getTechnologyUsesWithAssessments',
      technologyId,
    ],
  },
  userCompanies: {
    getCompanyUsers: ['users', 'getCompanyUsers'],
  },
  researchKeywords: {
    getResearchKeywords: ['researchKeywords', 'getResearchKeywords'],
  },
  userProfile: {
    getUserProfile: ['userProfile', 'getUserProfile'],
    getUserProfiles: ['userProfile', 'getUserProfiles'],
    searchUserProfiles: (referralCode?: string) => ['userProfile', 'searchUserProfiles', referralCode].filter(Boolean),
  },
  use: {
    getUses: ['use', 'getUses'],
  },
  tags: {
    root: ['tags'] as const,
    getTag: (id: string) => ['tags', 'getTag', id],
    getTags: (params?: { onlyUsedByCompanies?: boolean }) => ['tags', 'getTags', params],
  },
  intelTechnologies: {
    root: ['intelTechnologies'] as const,
    getTechnologyCompanies: ['intelTechnologies', 'getTechnologyCompanies'] as const,
    getTechnologies: (params: Record<string, unknown>) => ['intelTechnologies', 'getTechnologies', params],
  },
  intelOrganizations: {
    root: ['intelOrganizations'] as const,
    getOrganization: (companyId?: string) => ['intelOrganizations', 'getOrganization', companyId].filter(Boolean),
    getOrganizations: (params: Record<string, unknown>) => ['intelOrganizations', 'getOrganizations', params],
  },
  users: {
    getCurrentUser: ['users', 'getCurrentUser'],
  },
};
